import type { BlockType, CourseBlock } from './blocks';
import type { LessonThemeColor } from './course';
import type { JSONContent } from '@tiptap/core';
import type { MapType } from './maps';

export type TranslationType = 'mt' | 'ht';

export type TranslationRecordStatus =
  | 'not-translated'
  | 'mt'
  | 'mt-update-pending'
  | 'ht'
  | 'ht-update-pending';

export type TranslationReplacementAction = 'queue' | 'preserve';

export type TranslationRecordProvenance = 'exact' | 'inherited' | 'missing';

export type LessonBlockTranslationProvenance =
  | 'standard'
  | 'inherited'
  | 'override'
  | 'client-created'
  | 'suppressed';

export type TranslationNoticeTone = 'neutral' | 'info' | 'warning';

export interface TranslationStatusNote {
  text: string;
  tone: TranslationNoticeTone;
}

export interface TranslationScopeSummary {
  humanTranslationCount: number;
  pendingHumanTranslationCount: number;
}

export interface LessonMetadataTranslationProjection {
  lessonId: string;
  locale: string;
  accountId: number | null;
  provenance: TranslationRecordProvenance;
  exists: boolean;
  title: string;
  description: string;
  estimatedTimeMinutes?: number | null;
  themeColor?: LessonThemeColor | null;
  translationType: TranslationType | null;
  translationUpdatePending: boolean;
  status: TranslationRecordStatus | null;
  summary: TranslationScopeSummary;
  editable: boolean;
}

export interface LessonMetadataTranslationPatch {
  title?: string;
  description?: string;
  estimatedTimeMinutes?: number | null;
  themeColor?: LessonThemeColor | null;
  replacementAction?: TranslationReplacementAction;
}

export interface CourseMetadataTranslationProjection {
  courseId: string;
  locale: string;
  accountId: number | null;
  provenance: TranslationRecordProvenance;
  exists: boolean;
  title: string;
  description: string;
  certificateMessage: string;
  completedMessage: string;
  translationType: TranslationType | null;
  translationUpdatePending: boolean;
  status: TranslationRecordStatus | null;
  summary: TranslationScopeSummary;
  editable: boolean;
  editabilityReason: 'lifecycle-locked' | 'missing-client-english-source' | null;
}

export const COURSE_METADATA_TRANSLATED_FIELDS = [
  'title',
  'description',
  'certificateMessage',
  'completedMessage',
] as const;

export type CourseMetadataTranslatedField = typeof COURSE_METADATA_TRANSLATED_FIELDS[number];

export interface CourseMetadataTranslationPatch {
  title?: string;
  description?: string;
  certificateMessage?: string;
  completedMessage?: string;
  replacementAction?: TranslationReplacementAction;
}

export const MAP_TOP_LEVEL_TRANSLATED_FIELDS = [
  'title',
  'description',
  'interactionMessage',
] as const;

export const MAP_REGION_TRANSLATED_FIELDS = [
  'name',
  'description',
] as const;

export type MapTopLevelTranslatedField = typeof MAP_TOP_LEVEL_TRANSLATED_FIELDS[number];
export type MapRegionTranslatedField = typeof MAP_REGION_TRANSLATED_FIELDS[number];

export interface MapTopLevelTranslationFields {
  title: string;
  description: string;
  interactionMessage: string;
}

export interface MapRegionTranslationFields {
  name: string;
  description: JSONContent;
}

export interface MapTranslationRecord<TFields> {
  fields: TFields;
  exists: boolean;
  translationType: TranslationType | null;
  translationUpdatePending: boolean;
  status: TranslationRecordStatus | null;
  summary: TranslationScopeSummary;
  editable: boolean;
}

export type MapTopLevelTranslationRecord = MapTranslationRecord<MapTopLevelTranslationFields>;

export interface MapRegionTranslationRecord extends MapTranslationRecord<MapRegionTranslationFields> {
  regionId: string;
  regionKey: string;
}

export interface MapTranslationProjection {
  mapId: string;
  mapType: MapType;
  locale: string;
  topLevel: MapTopLevelTranslationRecord;
  regions: MapRegionTranslationRecord[];
}

export interface MapTopLevelTranslationPatch {
  fields?: Partial<MapTopLevelTranslationFields>;
  replacementAction?: TranslationReplacementAction;
}

export interface MapRegionTranslationPatch {
  fields?: Partial<MapRegionTranslationFields>;
  replacementAction?: TranslationReplacementAction;
}

export type TranslationRecordSubject = 'record' | 'block';

export type LessonBlockTranslationFields = Record<string, unknown>;

export interface LessonBlockTranslationRecord {
  blockId: string;
  type: BlockType;
  provenance: LessonBlockTranslationProvenance;
  sourceBlock: CourseBlock;
  fields: LessonBlockTranslationFields;
  exists: boolean;
  translationType: TranslationType | null;
  translationUpdatePending: boolean;
  status: TranslationRecordStatus | null;
  summary: TranslationScopeSummary;
  editable: boolean;
}

export interface LessonBlockTranslationProjection {
  lessonId: string;
  locale: string;
  accountId: number | null;
  blocks: LessonBlockTranslationRecord[];
}

export interface LessonBlockTranslationPatch {
  fields?: LessonBlockTranslationFields;
  replacementAction?: TranslationReplacementAction;
}

export interface LessonBlockContentPatch {
  block: CourseBlock;
  replacementAction?: TranslationReplacementAction;
}

export interface LessonBlockCreateRequest {
  clientBlockId: string;
  block: CourseBlock;
  targetIndex: number;
  /**
   * When creating a duplicate of an existing block, the id of the source
   * block whose non-English translations should be copied onto the new
   * block. Omitted for ordinary block creation.
   */
  sourceBlockId?: string;
}

export interface LessonBlockMoveRequest {
  targetIndex: number;
}

/** NA-437: copies a block from its current (Standard-context) lesson into a different, existing Emtrain lesson. */
export interface LessonBlockCopyRequest {
  sourceBlockId: string;
  block: CourseBlock;
  /** Defaults to 'end' server-side when omitted. */
  placement?: 'beginning' | 'end';
}

export interface LessonBlockCopyResponse {
  blockId: string;
  block: CourseBlock | null;
  blockOrder: string[];
}

export interface LessonBlockMutationResponse {
  block: CourseBlock | null;
  blockOrder: string[];
  translationSourceChanged?: boolean;
}

export interface LessonBlockCreateResponse extends LessonBlockMutationResponse {
  clientBlockId: string;
  blockId: string;
  block: CourseBlock;
}

export const TRANSLATION_STATUS_LABELS: Record<TranslationRecordStatus, string> = {
  'not-translated': 'Not translated',
  mt: 'MT',
  'mt-update-pending': 'MT — update pending',
  ht: 'HT',
  'ht-update-pending': 'HT — update pending',
};

export const TRANSLATION_STATUS_NOTES: Readonly<
  Partial<Record<TranslationRecordStatus, TranslationStatusNote>>
> = {
  'not-translated': {
    text: 'No translation yet. Adding text will create a human translation for this record.',
    tone: 'neutral',
  },
  mt: {
    text: 'This is a machine translation. Editing any field will convert this record to a human translation.',
    tone: 'info',
  },
  'mt-update-pending': {
    text: 'The English source was updated. This machine translation will be replaced automatically on the next scheduled run.',
    tone: 'info',
  },
  'ht-update-pending': {
    text: 'The English source was updated. This human translation will be replaced with a machine translation of the new English content on the next scheduled run.',
    tone: 'warning',
  },
};

export function getTranslationStatusNote(
  status: TranslationRecordStatus,
  subject: TranslationRecordSubject = 'record',
): TranslationStatusNote | undefined {
  const note = TRANSLATION_STATUS_NOTES[status];
  if (!note || subject === 'record') return note;
  return { ...note, text: note.text.replace(/\brecord\b/g, subject) };
}

export const TRANSLATION_REPLACEMENT_COPY = {
  guidance: "If you don't respond, the human translations will be preserved.",
  queued: 'Human translations queued for replacement. Badges will show HT — update pending.',
  preserved: 'Human translations preserved.',
} as const;

export function deriveTranslationRecordStatus({
  exists,
  translationType,
  translationUpdatePending,
}: {
  exists: boolean;
  translationType: TranslationType | null;
  translationUpdatePending: boolean;
}): TranslationRecordStatus {
  if (!exists || translationType === null) return 'not-translated';
  if (translationType === 'ht') {
    return translationUpdatePending ? 'ht-update-pending' : 'ht';
  }
  return translationUpdatePending ? 'mt-update-pending' : 'mt';
}

export function getTranslationReplacementPrompt(
  humanTranslationCount: number,
  subject: TranslationRecordSubject = 'record',
): string {
  const noun = humanTranslationCount === 1 ? 'translation' : 'translations';
  return `This ${subject} has ${humanTranslationCount} human-generated ${noun}. Should this edit trigger new machine translations that will replace the human translations?`;
}
